import { useCallback, useEffect, useRef, useState } from 'react';
import NextThing from './NextThing.jsx';
import RenewalLead from './RenewalLead.jsx';
import AccountHeader from './AccountHeader.jsx';
import TermsSection from './TermsSection.jsx';
import { TermEssentials, TermMoney } from './TermBlock.jsx';
import AircraftSection from './AircraftSection.jsx';
import PeopleSection from './PeopleSection.jsx';
import InterestsSection from './InterestsSection.jsx';
import DocumentsSection from './DocumentsSection.jsx';
import DropZone from './DropZone.jsx';
import CommunicationsSection from './CommunicationsSection.jsx';
import PhoneBar from './PhoneBar.jsx';
import { formatCurrency } from '@/lib/format.js';
import { formatHumanDate } from '@/lib/date-display.js';
import { AR_WORDS, AP_WORDS, NOT_RECORDED, NOT_READ_YET, MAIL_NOT_CONNECTED, PENDING_KIND_WORDS, SESSION_STATUS_WORDS, word } from './words.js';
import { NOT_CONNECTED } from '@/services/v3.service.js';

// THE PHONE COLUMN of the v3 account page (V3 overnight 1, Track C step 3,
// the Mobile and MobileSections sketches). Under 640 px the page is one
// column: the header, the next-thing line, the current term's essentials
// (carrier, period, policy number or its input, premium, state, and the
// coverage block TermEssentials carries from TermBlock, stage D1), then one
// row per family (Quotes, Aircraft, Pilots, Interests, Documents, Money,
// Conversation: the MobileSections sketch's seven), each a single-line
// summary that opens the family's section in place under the row. The sections are the same components the desktop page renders;
// nothing here is a second widget set, and nothing here derives a state: a
// summary is the payload's own words (the AR and AP word maps, counts,
// names), and a row's "open" chip counts the AIRgus items the page places
// on that family. The bottom bar of the sketch (Next up, Search, Accounts,
// Money) is PhoneBar (stage D4): Next up scrolls to the next-thing line,
// Money opens the Money row, Search fires the palette's event, Accounts is a
// route; it claims the bottom from the shell's tab bar while mounted. The
// side gutter is a literal 16px: px-4 is one rem, and the app's base type is
// 17px.

export const PHONE_QUERY = '(max-width: 639px)';
export const FAMILIES = Object.freeze(['quotes', 'aircraft', 'pilots', 'interests', 'documents', 'money', 'communications']);
export const FAMILY_LABELS = Object.freeze({ quotes: 'Quotes', aircraft: 'Aircraft', pilots: 'Pilots', interests: 'Interests', documents: 'Documents', money: 'Money', communications: 'Conversation' });
export const NO_CURRENT_TERM = 'no current term';

// Term fields the essentials card renders (an item on them sits there), and
// the money fields (an item on them counts under Money); every other term
// field counts under Quotes.
const ESSENTIAL_FIELDS = new Set(['policy_number', 'carrier', 'effective_date', 'expiration_date']);
const MONEY_FIELDS = new Set(['premium', 'estimated_taxes', 'fees', 'commission', 'billing_method', 'ar_payment_status', 'ap_remit_status', 'carrier_confirmed_at']);

// Which phone family an item's fact_ref lands under; null when it lands on
// the header (account) or the essentials card.
export function familyOf(ref) {
  if (!ref || !ref.subject) return null;
  switch (ref.subject) {
    case 'aircraft': return 'aircraft';
    case 'person': return 'pilots';
    case 'interest': return 'interests';
    case 'document': return 'documents';
    case 'term': return ESSENTIAL_FIELDS.has(ref.field) ? null : MONEY_FIELDS.has(ref.field) ? 'money' : 'quotes';
    default: return null;
  }
}

function list(names) { return names.filter(Boolean).join(', '); }

// One line per family, the payload's own words.
// A pending term leads the Quotes row, so a renewal that exists only as a session is not hidden behind a closed row.
export function pendingSummary(pending = []) {
  if (!pending.length) return null;
  const p = pending[0];
  const when = p.started_on ? ` ${formatHumanDate(p.started_on)}` : '';
  return `${word(PENDING_KIND_WORDS, p.kind)}${when}, ${word(SESSION_STATUS_WORDS, p.status).toLowerCase()}${pending.length > 1 ? ` (+${pending.length - 1} more)` : ''}`;
}

export function familySummaries({ term, aircraft = [], people = [], interests = [], documents = [], conversation = null, pending = [] }) {
  const counts = conversation?.data?.counts || null;
  const q = term?.quotes || null;
  const bind = term?.ledger?.bind || null;
  const pilots = people.filter((p) => p.pilot);
  const regs = aircraft.map((a) => a.registration);
  return {
    quotes: pendingSummary(pending) || (!term ? NO_CURRENT_TERM : q == null ? NOT_RECORDED : q.count ? `${q.count} received, ${q.presented} presented${q.low_premium != null ? `, low ${formatCurrency(q.low_premium)} ${q.low_carrier || ''}`.trimEnd() : ''}${q.bound_quote_id ? ', one bound' : ''}` : 'none on this term'),
    aircraft: aircraft.length === 0 ? 'none on this account' : aircraft.length === 1 ? `${regs[0]} · ${[aircraft[0].year, aircraft[0].make, aircraft[0].model].filter(Boolean).join(' ') || NOT_RECORDED}` : list(regs),
    pilots: pilots.length === 0 ? 'none on this account' : `${pilots.length}: ${list(pilots.map((p) => p.display_name))}`,
    interests: interests.length === 0 ? 'none on this account' : list(interests.map((i) => i.name)),
    documents: documents.length === 0 ? 'none on file' : `${documents.length} on file, newest ${documents[0].label || documents[0].kind || NOT_RECORDED}`,
    money: !term ? NO_CURRENT_TERM : !bind ? 'no live bind' : `A/R ${word(AR_WORDS, bind.ar_payment_status)} · A/P ${word(AP_WORDS, bind.ap_remit_status)}`,
    communications: conversation?.source === NOT_CONNECTED ? MAIL_NOT_CONNECTED : conversation?.error ? `could not read: ${conversation.error}` : !counts ? NOT_READ_YET : counts.messages === 0 ? 'none on this account' : `${counts.messages} ${counts.messages === 1 ? 'message' : 'messages'}${counts.unattributed ? `, ${counts.unattributed} unattributed` : ''}`,
  };
}

export function familyItemCounts(byRef) {
  const counts = Object.fromEntries(FAMILIES.map((f) => [f, 0]));
  for (const [k, items] of byRef.entries()) {
    const [subject, , field] = k.split(':');
    const f = familyOf({ subject, field });
    if (f) counts[f] += items.length;
  }
  return counts;
}

function FamilyRow({ family, summary, count, open, onToggle }) {
  return (
    <button type="button" onClick={onToggle} aria-expanded={open} className="flex min-h-[52px] w-full items-center gap-3 border-b border-border px-4 text-left last:border-b-0 hover:bg-border/20" data-family={family} data-open={open ? 'true' : 'false'}>
      <span className="w-24 shrink-0 text-sm font-semibold text-text">{FAMILY_LABELS[family]}</span>
      <span className="min-w-0 flex-1 truncate text-[13px] text-text-muted" data-family-summary>{summary}</span>
      {count ? <span className="shrink-0 rounded-pill bg-warning-soft px-2 py-0.5 text-[11px] font-medium text-warning" data-family-open-count>{count} open</span> : null}
      <span className="shrink-0 text-xs text-text-dim" aria-hidden="true">{open ? '▾' : '▸'}</span>
    </button>
  );
}

export default function PhoneLayout({ data, badge, loose, focusTermId, common, conversation = null, docsConnected = null }) {
  const { byRef, ctx, onSaved, remedyHref } = common;
  const terms = data.terms || [];
  const term = terms.find((t) => t.id === data.current_term_id) || null;
  const [openFamily, setOpenFamily] = useState(focusTermId ? 'quotes' : null);
  useEffect(() => { if (focusTermId) setOpenFamily('quotes'); }, [focusTermId]);
  // A remedy that names a row of an OLDER term (#fact-term-<id>-<field>): that term's rows live in the Quotes family, so open it; the
  // Terms section inside then opens the term itself (TermsSection.jsx).
  useEffect(() => {
    const openNamed = () => {
      const m = /^#fact-term-(.+)-([a-z_]+)$/.exec(window.location.hash || '');
      if (m && m[1] !== data.current_term_id && (data.terms || []).some((t) => t.id === m[1])) setOpenFamily('quotes');
    };
    openNamed();
    window.addEventListener('hashchange', openNamed);
    return () => window.removeEventListener('hashchange', openNamed);
  }, [data.terms, data.current_term_id]);
  const toggle = (f) => setOpenFamily((o) => (o === f ? null : f));
  const nextRef = useRef(null);
  const familyRefs = useRef({});
  // The bar's section doors: 'next' scrolls to the next-thing line; a family
  // name opens that row and scrolls to it. Nothing else is a section here.
  const openSection = useCallback((section) => {
    if (section === 'next') { nextRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
    if (!FAMILIES.includes(section)) return;
    setOpenFamily(section);
    familyRefs.current[section]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  const summaries = familySummaries({ term, aircraft: data.aircraft || [], people: data.people || [], interests: data.interests || [], documents: data.documents || [], conversation, pending: data.pending_terms || [] });
  const counts = familyItemCounts(byRef);
  const sectionProps = { byRef, ctx, onSaved, remedyHref };
  const body = (f) => {
    switch (f) {
      case 'quotes': return <TermsSection terms={terms} pending={data.pending_terms || []} currentTermId={data.current_term_id} focusTermId={focusTermId} people={data.people || []} conversation={conversation} {...sectionProps} />;
      case 'aircraft': return <AircraftSection aircraft={data.aircraft || []} {...sectionProps} />;
      case 'pilots': return <PeopleSection people={data.people || []} {...sectionProps} />;
      case 'interests': return <InterestsSection interests={data.interests || []} byRef={byRef} remedyHref={remedyHref} />;
      case 'documents': return <DocumentsSection documents={data.documents || []} terms={terms} byRef={byRef} remedyHref={remedyHref} />;
      case 'money': return term ? (
        <article className="rounded-card border border-border bg-surface" data-phone-money={term.id}>
          <div className="px-4"><TermMoney t={term} /></div>
        </article>
      ) : <p className="px-4 py-3 text-xs italic text-text-dim">{NO_CURRENT_TERM}</p>;
      case 'communications': return <CommunicationsSection conversation={conversation} terms={terms} />;
      default: return null;
    }
  };
  return (
    <div className="space-y-3 px-[16px] py-3 pb-16" data-v3-phone>
      {badge}
      <AccountHeader account={data.account} people={data.people || []} related={data.related_accounts || []} compact payload={data} {...sectionProps} />
      <div ref={nextRef}><NextThing airgus={data.airgus} loose={loose} remedyHref={remedyHref} /></div>
      <RenewalLead data={data} account={data.account} />
      {term ? (
        <TermEssentials t={term} byRef={byRef} ctx={ctx} onSaved={onSaved} remedyHref={remedyHref} onOpen={() => setOpenFamily('quotes')} />
      ) : (
        <p className="rounded-card border border-border bg-surface px-4 py-3 text-sm text-text-dim" data-term-essentials="none">{NO_CURRENT_TERM}</p>
      )}
      <section aria-label="Account sections" className="rounded-card border border-border bg-surface" data-family-rows>
        {FAMILIES.map((f) => (
          <div key={f} ref={(el) => { familyRefs.current[f] = el; }} data-family-block={f}>
            <FamilyRow family={f} summary={summaries[f]} count={counts[f]} open={openFamily === f} onToggle={() => toggle(f)} />
            {openFamily === f ? <div className="border-b border-border bg-bg px-2 py-2" data-family-body={f}>{body(f)}</div> : null}
          </div>
        ))}
      </section>
      <DropZone docsConnected={docsConnected} account={data.account} terms={terms} interests={data.interests || []} currentTermId={data.current_term_id || null} onSaved={onSaved} />
      <PhoneBar openSection={openSection} />
    </div>
  );
}
